/**
 * Masthead greetings (brief phase B §B5 / walk D10) — the brief's opening line.
 *
 * Selection is DETERMINISTIC and LLM-FREE. Every input is a signal the page below already
 * computed and already renders, so a greeting can never contradict the page it sits on top of.
 *
 * Cycling adds no persistence: `index = hash(readerId + briefDate) % list.length`, shifted one
 * step when it collides with yesterday's index computed the same way. That makes the line stable
 * on reload, different for two readers on the same day, non-repeating two days running, and —
 * because the key is the BRIEF's own UTC date, never "today" and never the reader's timezone —
 * an archived brief re-renders its ORIGINAL greeting instead of rewriting the past.
 *
 * The copy is Ryan's, approved 2026-07-31 (round 4). Do not add, edit, or reorder lines.
 */

export interface Greeting {
  headline: string;
  standfirst: string;
}

export type GreetingBucket = 'solo' | 'quiet' | 'heavy' | 'waiting' | 'meetings' | 'plain';

/** The day/team shapes the surface can observe. All five are read off rendered values. */
export interface GreetingSignals {
  /** coverage present and `connected <= 1` — the reader is effectively alone on the team. */
  solo: boolean;
  /** the payload's `quietDay`, or no tasks and no changes on the page. */
  quiet: boolean;
  /** tasks ran over the server's inline budget (the column's "+N below today's threshold" tail). */
  heavy: boolean;
  /** a conflict is holding one of the priority column's slots. */
  waiting: boolean;
  /** today's calendar rail carries at least MEETING_DENSE_MIN holds. */
  meetingDense: boolean;
}

export interface GreetingInput {
  /** The reader's Supabase user id; '' / null still selects, just without per-reader spread. */
  readerId: string | null;
  /** The BRIEF's own calendar day in UTC, `YYYY-MM-DD` (see briefDateKey). Never "today". */
  briefDate: string;
  signals: GreetingSignals;
  /** Is this the CURRENT day's brief? The overlay keys on the reader's clock, which an archived
   * brief has no claim to — so only a live brief is overlay-eligible. */
  isLive: boolean;
  /** The reader's local clock at render (overlay only). */
  now: Date;
}

/** Today's rail counts as dense at three holds — the same "a full column" unit the priority
 * column next to it is capped at (3 inline slots). Below that the rail is mostly free time. */
export const MEETING_DENSE_MIN = 3;

export const GREETINGS: Record<GreetingBucket, Greeting[]> = {
  plain: [
    { headline: 'The plot advances.', standfirst: 'Small moves on every front. Details below.' },
    { headline: 'Same river, new water.', standfirst: 'Familiar projects, fresh movement.' },
    {
      headline: "Nothing's on fire.",
      standfirst: 'A normal day — a few things worth a look, none of them urgent.',
    },
    { headline: 'Onward.', standfirst: 'Everything below is mid-story. Pick up where the team left off.' },
    { headline: 'While you were away.', standfirst: "The team kept going. Here's the readable version." },
    { headline: 'Middle chapters.', standfirst: "Where the real work happens. Today's pages below." },
    { headline: 'Fresh ink.', standfirst: 'What the team wrote since yesterday, distilled.' },
    { headline: 'Ticking along.', standfirst: 'Things are moving about how they should. A few are yours.' },
  ],
  quiet: [
    { headline: 'Crickets.', standfirst: 'Almost nothing came in overnight. Rare. Spend it well.' },
    { headline: 'Deep work weather.', standfirst: "The team's heads-down, so the page is short." },
    { headline: 'Still water.', standfirst: 'Nothing stirred overnight. A good day to go deep.' },
    { headline: "You've outrun the news.", standfirst: 'Nothing new to report. Go make some.' },
    {
      headline: 'All quiet, all good.',
      standfirst: 'Your sources are connected and reporting — there was just less to say.',
    },
  ],
  heavy: [
    {
      headline: 'Grab a coffee first.',
      standfirst: "A lot landed overnight. It's ranked — the top really is the top.",
    },
    {
      headline: 'The team got loud.',
      standfirst: 'Busy twelve hours around you. The parts that reach you are below.',
    },
    { headline: 'Everything, everywhere.', standfirst: 'All at once, apparently. Start where the page starts.' },
    {
      headline: 'Inbox weather: pouring.',
      standfirst: "More than usual came through. Umbrella's the priority column.",
    },
    { headline: 'All hands were busy.', standfirst: 'Plenty of movement — your share of it is pulled forward.' },
  ],
  waiting: [
    { headline: 'Two roads diverged.', standfirst: "Your team took both. Someone has to pick — that's below." },
    {
      headline: "You're the tiebreaker.",
      standfirst: 'One question, two positions, and your read would settle it.',
    },
    { headline: 'Deadlock, meet daylight.', standfirst: 'An open call is holding up the work behind it.' },
    { headline: 'One knot to untie.', standfirst: 'Loosen it and the rest of the day runs smoother.' },
  ],
  meetings: [
    { headline: 'Calendar tetris.', standfirst: 'Stacked rooms today. Prep for each is underneath it.' },
    { headline: 'Talking day.', standfirst: "Rooms and people, mostly — with what's unresolved going into each." },
    { headline: 'Booked solid.', standfirst: "The digging you'd have done before each meeting is already done." },
    { headline: 'Bring the good pen.', standfirst: 'A day of rooms — notes on each one, ready before you walk in.' },
  ],
  solo: [
    { headline: 'One-player mode.', standfirst: 'Just you today — and every step is on the record.' },
    { headline: 'Party of one.', standfirst: "Today's trail is all yours. It reads well." },
    { headline: 'Head down, lights on.', standfirst: "You worked, it's written down. The system's doing its job." },
    { headline: 'The quiet shift.', standfirst: "Just you on today. It all counts — it's all below." },
    { headline: 'Building in the quiet.', standfirst: "No audience yet. The record's being kept anyway." },
    { headline: 'Your story so far.', standfirst: "Nobody else checked in — here's your own day, kept safe." },
  ],
};

/** Time-of-day / week lines. One line per condition — no cycling, so no hash. */
export const OVERLAY_GREETINGS: Record<'night' | 'early' | 'monday' | 'friday', Greeting> = {
  night: {
    headline: 'Hey, night owl.',
    standfirst: "Tomorrow's brief, early. Or today's, late — no judgment.",
  },
  early: {
    headline: 'Up with the birds.',
    standfirst: "The overnight sweep just finished. You're its first reader.",
  },
  monday: { headline: 'Monday, huh.', standfirst: "Whatever the weekend did, it's sorted below." },
  friday: {
    headline: 'Almost the weekend.',
    standfirst: 'Short list. Nothing here will chase you into Saturday.',
  },
};

/**
 * Bucket precedence — ONE ordered list, total by construction.
 *
 * `solo` leads: `coverage.connected <= 1` says the reader is the only connected person on the
 * team, and a solo reader's page IS the solo story — the reassurance that their own work is being
 * kept outranks the day's shape.
 *
 * `quiet` sits LAST of the shapes (phase-B review F8). Its predicate looks at tasks and changes
 * only — never at meetings, conflicts or relevant cards — so ranking it high let "Crickets."
 * render above a full calendar rail or a conflict the reader has to settle, which is exactly the
 * contradiction this module exists to prevent. Every busier shape gets to speak first; quiet fires
 * only when nothing else did.
 */
export function greetingBucket(signals: GreetingSignals): GreetingBucket {
  if (signals.solo) return 'solo';
  if (signals.heavy) return 'heavy';
  if (signals.waiting) return 'waiting';
  if (signals.meetingDense) return 'meetings';
  if (signals.quiet) return 'quiet';
  return 'plain';
}

/** The brief's calendar day, `YYYY-MM-DD`, in UTC. '' when the timestamp is absent/unparseable
 * (selection stays deterministic, it just loses the day-to-day cycle).
 *
 * UTC, not the reader's local day (phase-B review F9): the key IS the brief's identity for
 * selection, so reading one archived brief from Tokyo and from Los Angeles has to land on the same
 * line — a local key puts a late-evening brief on two different dates and rewrites the past. The
 * surface payload carries no server-side date string of its own on the live route, so the
 * timestamp's UTC day is the one value both routes can agree on. */
export function briefDateKey(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/** The greeting this reader gets for this brief. Pure: same inputs, same line, forever. */
export function pickGreeting({ readerId, briefDate, signals, isLive, now }: GreetingInput): Greeting {
  const bucket = greetingBucket(signals);
  // The overlay is a PLAIN-day flourish only: a day-shape signal always outranks the clock, and an
  // archived brief has no "now" of its own to key on.
  if (bucket === 'plain' && isLive) {
    const over = overlayFor(now);
    if (over) return over;
  }
  const list = GREETINGS[bucket];
  return list[cyclingIndex(readerId ?? '', briefDate, list.length)];
}

// ---- internals ----

const pad = (n: number) => String(n).padStart(2, '0');

/** djb2 (xor variant), unsigned. Small, stable, and dependency-free — the index only needs to be
 * evenly spread and identical on every machine, not cryptographic. */
function hash32(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h * 33) ^ s.charCodeAt(i)) >>> 0;
  return h >>> 0;
}

/** The calendar day before `key`. UTC arithmetic on the plain Y-M-D triple, so DST can't shift it. */
function previousDateKey(key: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) - 86_400_000);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/** hash(reader + briefDate) % len, shifted one step off yesterday's index in the SAME list.
 * Cross-bucket collisions are fine by design — the guarantee is "not the same line two days
 * running when the day has the same shape". */
function cyclingIndex(readerId: string, briefDate: string, len: number): number {
  const idx = hash32(`${readerId}|${briefDate}`) % len;
  const prev = previousDateKey(briefDate);
  if (!prev) return idx;
  const yesterday = hash32(`${readerId}|${prev}`) % len;
  return idx === yesterday ? (idx + 1) % len : idx;
}

/** The clock outranks the weekday (a Monday night owl is reading at night, first). */
function overlayFor(now: Date): Greeting | null {
  const hour = now.getHours();
  if (hour >= 22 || hour < 5) return OVERLAY_GREETINGS.night;
  if (hour < 7) return OVERLAY_GREETINGS.early;
  const day = now.getDay();
  if (day === 1) return OVERLAY_GREETINGS.monday;
  if (day === 5) return OVERLAY_GREETINGS.friday;
  return null;
}
